/**
 * Cycle de vie d'une connexion WebSocket « arbitrage ».
 *
 * Extrait de `DealsList` et `AgencyDealsList`, où il était dupliqué à
 * l'identique et où il fuyait : le `return () => socket.close()` était retourné
 * par la fonction interne `connect()`, pas par le callback du `useEffect`.
 * L'effet retournait donc `undefined`, et à chaque démontage la socket, son
 * heartbeat 30 s et sa boucle de reconnexion survivaient puis s'empilaient —
 * une instance de plus par navigation vers la liste des deals.
 *
 * Le module n'importe pas React volontairement : il est ainsi testable en
 * environnement `node` en injectant `createSocket`.
 */

export interface ArbitrationSocketLike {
  readyState: number;
  send: (data: string) => void;
  close: () => void;
  onopen: (() => void) | null;
  onclose: (() => void) | null;
  onerror: ((error: unknown) => void) | null;
  onmessage: ((event: { data: string }) => void) | null;
}

export interface ArbitrationSocketMessage {
  type: string;
  /**
   * Le backend envoie une forme différente selon `type` (deal, arbitration…) et
   * aucun schéma n'est partagé entre les deux packages : `any` est assumé ici
   * plutôt qu'un type inventé qui mentirait sur le contrat réel.
   */
  data?: any;
}

export interface OpenArbitrationSocketOptions {
  url: string;
  onMessage: (message: ArbitrationSocketMessage) => void;
  /** Injecté par les tests ; en production, une vraie `WebSocket`. */
  createSocket?: (url: string) => ArbitrationSocketLike;
  heartbeatIntervalMs?: number;
  reconnectDelayMs?: number;
}

export const HEARTBEAT_INTERVAL_MS = 30_000;
export const RECONNECT_DELAY_MS = 5_000;

/** Constantes `WebSocket.OPEN` / `WebSocket.CLOSED`, redéclarées pour rester hors DOM. */
const WEBSOCKET_OPEN = 1;
const WEBSOCKET_CLOSED = 3;

/**
 * `WebSocket` expose ses handlers avec les types DOM (`MessageEvent`, `Event`),
 * plus larges que ceux dont ce module a besoin. Le cast évite de traîner ces
 * types dans l'interface testable.
 */
const defaultCreateSocket = (url: string): ArbitrationSocketLike =>
  new WebSocket(url) as unknown as ArbitrationSocketLike;

/**
 * Ouvre la connexion et retourne son teardown. Appeler le teardown ferme la
 * socket, annule le heartbeat et annule toute reconnexion en attente — après
 * quoi plus rien ne se reprogramme.
 */
export const openArbitrationSocket = (options: OpenArbitrationSocketOptions): (() => void) => {
  const {
    url,
    onMessage,
    createSocket = defaultCreateSocket,
    heartbeatIntervalMs = HEARTBEAT_INTERVAL_MS,
    reconnectDelayMs = RECONNECT_DELAY_MS,
  } = options;

  const state: {
    stopped: boolean;
    socket: ArbitrationSocketLike | null;
    heartbeat: ReturnType<typeof setInterval> | null;
    reconnect: ReturnType<typeof setTimeout> | null;
  } = { stopped: false, socket: null, heartbeat: null, reconnect: null };

  const clearHeartbeat = (): void => {
    if (state.heartbeat === null) return;
    clearInterval(state.heartbeat);
    state.heartbeat = null;
  };

  const clearReconnect = (): void => {
    if (state.reconnect === null) return;
    clearTimeout(state.reconnect);
    state.reconnect = null;
  };

  /**
   * Coupe définitivement une socket : plus aucun handler ne peut la faire
   * reprogrammer quoi que ce soit, et elle est fermée si elle ne l'est pas déjà.
   */
  const releaseSocket = (socket: ArbitrationSocketLike): void => {
    socket.onopen = null;
    socket.onclose = null;
    socket.onerror = null;
    socket.onmessage = null;
    if (socket.readyState !== WEBSOCKET_CLOSED) socket.close();
  };

  const connect = (): void => {
    if (state.stopped) return;

    // Une socket précédente ne doit jamais rester branchée : son `onclose`
    // tardif programmerait une seconde boucle de reconnexion en parallèle.
    if (state.socket !== null) releaseSocket(state.socket);

    const socket = createSocket(url);
    state.socket = socket;
    state.reconnect = null;

    socket.onopen = () => {
      console.log('Connected to WebSocket server');
      // Heartbeat : maintient la connexion ouverte à travers les proxies et
      // load balancers qui coupent les sockets inactives.
      clearHeartbeat();
      state.heartbeat = setInterval(() => {
        // La socket peut être passée en CLOSING entre deux tics : `send()`
        // lèverait alors une InvalidStateError.
        if (socket.readyState !== WEBSOCKET_OPEN) return;
        socket.send(JSON.stringify({ type: 'heartbeat' }));
      }, heartbeatIntervalMs);
    };

    socket.onclose = () => {
      clearHeartbeat();
      // Le teardown détache les handlers et pose `stopped` avant de fermer :
      // une fermeture provoquée par le démontage ne relance jamais la boucle.
      if (state.stopped) return;
      console.log('WebSocket connection closed. Reconnecting...');
      state.reconnect = setTimeout(connect, reconnectDelayMs);
    };

    socket.onerror = (error: unknown) => {
      console.error('arbitrationSocket / erreur WebSocket:', error);
      socket.close();
    };

    socket.onmessage = (event: { data: string }) => {
      try {
        onMessage(JSON.parse(event.data) as ArbitrationSocketMessage);
      } catch (error) {
        // Une trame illisible ne doit pas casser le handler ni tuer la socket.
        console.error('arbitrationSocket / message WebSocket illisible:', error);
      }
    };
  };

  connect();

  return () => {
    state.stopped = true;
    clearHeartbeat();
    clearReconnect();

    const socket = state.socket;
    state.socket = null;
    if (socket === null) return;

    releaseSocket(socket);
  };
};
